import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Icon,
  Popconfirm,
  Progress,
  Space,
  Spin,
  Tag,
  Text,
  Title,
  useToast,
} from "@app/ui";
import { ApiError } from "../api/client.js";
import type { FieldIssue } from "@app/core/validation";
import { archivePassport, getPassport, getProduct, publishPassport } from "../api/dpp.js";
import { useCan } from "../auth/useCan.js";
import { ErrorAlert, resolveFieldMessage } from "../errors/index.js";
import { useLang, useT, useFormatDateTime } from "../i18n.js";
import { NoOrgState } from "../orgs/NoOrgState.js";
import { useOrgs } from "../orgs/OrgContext.js";
import { fieldLabel, getSchemaDef } from "../dpp/schema-registry.js";
import { AccessGrantsCard } from "../dpp/AccessGrantsCard.js";
import { MetaRow } from "../dpp/MetaRow.js";
import { PassportLineageCard } from "../dpp/PassportLineageCard.js";
import { PassportPublicAccessCard } from "../dpp/PassportPublicAccessCard.js";
import { PassportRegistryCard } from "../dpp/PassportRegistryCard.js";
import { PassportRetentionCard } from "../dpp/PassportRetentionCard.js";
import { PassportVersionsCard } from "../dpp/PassportVersionsCard.js";
import { createLatestGate } from "../api/latest.js";
import type { PassportDetail, PassportStatus, ProductDetail, ReadinessIssue } from "../types.js";

const STATUS_COLOR: Record<PassportStatus, "default" | "success" | "warning"> = {
  draft: "warning",
  published: "success",
  archived: "default",
};

/** Max missing-field chips before the rest collapse into a "+N more" chip. */
const MISSING_CHIP_LIMIT = 8;

type CheckTone = "ok" | "warn" | "idle";

const CHECK_ICON: Record<CheckTone, "success" | "warning" | "info"> = {
  ok: "success",
  warn: "warning",
  idle: "info",
};

const CHECK_COLOR: Record<CheckTone, string> = {
  ok: "text-[var(--az-success)]",
  warn: "text-[var(--az-warning)]",
  idle: "text-muted-foreground",
};

/** One line of the publish-readiness checklist. */
function CheckLine({ tone, children }: { tone: CheckTone; children: ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      <Icon name={CHECK_ICON[tone]} size={15} className={`mt-0.5 shrink-0 ${CHECK_COLOR[tone]}`} />
      <span className={tone === "idle" ? "text-muted-foreground" : undefined}>{children}</span>
    </li>
  );
}

export function PassportDetailPage() {
  const t = useT();
  const { lang } = useLang();
  const fmtDate = useFormatDateTime();
  const toast = useToast();
  const can = useCan();
  const { org, loading: orgsLoading } = useOrgs();
  const { id } = useParams<{ id: string }>();

  const [passport, setPassport] = useState<PassportDetail | null>(null);
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [publishIssues, setPublishIssues] = useState<{ field: string; message: string }[] | null>(null);
  const [publishing, setPublishing] = useState(false);

  const canPublish = can("passport:publish");
  const canWrite = can("passport:write");
  const canManageAccess = can("access:manage");
  const def = passport ? getSchemaDef(passport.schemaId) : undefined;

  // Switching org/passport mid-load must not let the older response land last.
  const gate = useRef(createLatestGate()).current;

  const load = async (orgId: string, passportId: string) => {
    const token = gate.begin();
    setError(null);
    try {
      const detail = await getPassport(orgId, passportId);
      if (!gate.isCurrent(token)) return;
      setPassport(detail);
      // The passport carries only a productId — the product supplies the name,
      // SKU and identifiers the header and identity panel are built from. A
      // failure here must not blank the page, so it resolves on its own.
      getProduct(orgId, detail.productId)
        .then((p) => gate.isCurrent(token) && setProduct(p))
        .catch(() => gate.isCurrent(token) && setProduct(null));
    } catch (e) {
      if (gate.isCurrent(token)) setError(e);
    }
  };

  useEffect(() => {
    if (org && id) void load(org.id, id);
    return () => gate.cancel();
  }, [org?.id, id]);

  if (orgsLoading) return <Spin />;
  if (!org) return <NoOrgState />;
  if (error && !passport)
    return <ErrorAlert error={error} onRetry={() => org && id && void load(org.id, id)} />;
  if (!passport) return <Spin />;

  const publishedScore = passport.latestVersion?.complianceScore ?? null;
  const currentScore = passport.productCompliance.score;
  const drift = publishedScore !== null && publishedScore !== currentScore;
  const missingLabels = passport.productCompliance.missingRequired.map((f) => fieldLabel(def, f, lang));
  const registration = passport.registry.registration;
  const isPublished = passport.status === "published";
  const isArchived = passport.status === "archived";
  const hasVersion = passport.currentVersionNo !== null;
  const publicUrl = `${window.location.origin}/p/${encodeURIComponent(passport.publicSlug)}`;
  const identifier = product?.identifiers[0];
  const openLabel = product ? `${product.name} (${product.sku})` : passport.publicSlug;

  const publish = async () => {
    setPublishIssues(null);
    setError(null);
    setPublishing(true);
    try {
      await publishPassport(org.id, passport.id);
      toast.success(t("passport.published"));
      await load(org.id, passport.id);
    } catch (e) {
      if (e instanceof ApiError && e.status < 500) {
        const details = (e.details ?? {}) as {
          issues?: FieldIssue[];
          fields?: string[];
          readiness?: ReadinessIssue[];
        };
        if (details.readiness?.length) {
          // Cross-field readiness rules carry their own localized message.
          setPublishIssues(
            details.readiness.map((issue) => ({ field: issue.field ?? "", message: issue.message[lang] })),
          );
        } else if (details.issues?.length) {
          // Localize each publish-blocking field's message through the shared resolver.
          setPublishIssues(
            details.issues.map((issue) => ({
              field: issue.field,
              message: resolveFieldMessage(issue, lang),
            })),
          );
        } else if (details.fields?.length) {
          setPublishIssues(details.fields.map((field) => ({ field, message: "" })));
        } else {
          setError(e);
        }
      } else {
        setError(e);
      }
    } finally {
      setPublishing(false);
    }
  };

  const archive = async () => {
    setError(null);
    try {
      await archivePassport(org.id, passport.id);
      toast.success(t("passport.archived"));
      await load(org.id, passport.id);
    } catch (e) {
      setError(e);
    }
  };

  const reload = () => void load(org.id, passport.id);

  return (
    <div className="flex flex-col gap-5">
      {/* Page header — the passport's identity and its whole action set, so the
          cards below can stay purely informational. */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-0">
          <Link
            to="/passports"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <Icon name="chevron-left" size={14} />
            {t("passport.backToList")}
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Title level={3} className="truncate">
              {product?.name ?? t("passports.title")}
            </Title>
            <Tag color={STATUS_COLOR[passport.status]}>{t(`passportStatus.${passport.status}`)}</Tag>
            {hasVersion && <Tag color="primary">v{passport.currentVersionNo}</Tag>}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            {product && <span className="font-mono">{product.sku}</span>}
            {product && <span aria-hidden>·</span>}
            <span>{def ? def.title[lang] : passport.schemaId}</span>
            <span aria-hidden>·</span>
            <span>
              {t("common.updatedAt")}: {fmtDate(passport.updatedAt)}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {canPublish && (
            <Button
              variant="primary"
              loading={publishing}
              disabled={isArchived}
              icon={<Icon name="upload" size={14} />}
              onClick={() => void publish()}
            >
              {hasVersion ? t("passport.republish") : t("passport.publish")}
            </Button>
          )}
          {isPublished && (
            <Button
              icon={<Icon name="eye" size={14} />}
              onClick={() => window.open(`/p/${passport.publicSlug}`, "_blank", "noopener")}
            >
              {t("passport.publicPage")}
            </Button>
          )}
          {canWrite && !isArchived && (
            <Popconfirm
              title={t("passport.archiveConfirm")}
              description={t("passport.archiveDescription")}
              okText={t("common.archive")}
              cancelText={t("common.cancel")}
              okType="danger"
              onConfirm={() => void archive()}
            >
              {/* Destructive, rarely used — kept as a quiet control so it can't be
                  mistaken for the primary action next to it. */}
              <Button
                variant="subtle"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                icon={<Icon name="trash" size={14} />}
              >
                {t("common.archive")}
              </Button>
            </Popconfirm>
          )}
        </div>
      </div>

      {error != null && <ErrorAlert error={error} />}

      {publishIssues && (
        <Alert type="error">
          <Space direction="vertical" size="sm">
            <Text small>{t("passport.publishBlocked")}</Text>
            {publishIssues.map((issue, i) => (
              <Text small key={`${issue.field}-${i}`}>
                • {fieldLabel(def, issue.field, lang)}
                {issue.message ? ` — ${issue.message}` : ""}
              </Text>
            ))}
          </Space>
        </Alert>
      )}

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex min-w-0 flex-col gap-4">
          <Card title={t("passport.readiness")}>
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex flex-col items-center gap-1">
                <Progress
                  type="circle"
                  percent={currentScore}
                  size={92}
                  strokeWidth={8}
                  status={currentScore === 100 ? "success" : "normal"}
                />
                <Text small secondary>
                  {t("passport.productCompliance")}
                </Text>
              </div>

              <dl className="grid flex-1 grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-xs text-muted-foreground">{t("passport.currentVersion")}</dt>
                  <dd className="mt-0.5 font-medium tabular-nums">
                    {hasVersion ? `v${passport.currentVersionNo}` : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t("passport.publishedCompliance")}</dt>
                  <dd className="mt-0.5 font-medium tabular-nums">
                    {publishedScore === null ? "—" : `${publishedScore}%`}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t("common.createdAt")}</dt>
                  <dd className="mt-0.5">{fmtDate(passport.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t("common.updatedAt")}</dt>
                  <dd className="mt-0.5">{fmtDate(passport.updatedAt)}</dd>
                </div>
              </dl>
            </div>

            <ul className="mt-5 space-y-2 border-t border-border pt-4">
              {missingLabels.length === 0 ? (
                <CheckLine tone="ok">{t("passport.checkComplete")}</CheckLine>
              ) : (
                <CheckLine tone="warn">
                  {t("passport.checkMissing", { count: missingLabels.length })}
                </CheckLine>
              )}
              {hasVersion ? (
                <CheckLine tone="ok">
                  {t("passport.checkPublished", { version: String(passport.currentVersionNo) })}
                </CheckLine>
              ) : (
                <CheckLine tone="idle">{t("passport.checkUnpublished")}</CheckLine>
              )}
              {hasVersion &&
                (drift ? (
                  <CheckLine tone="warn">{t("passport.drift")}</CheckLine>
                ) : (
                  <CheckLine tone="ok">{t("passport.checkFresh")}</CheckLine>
                ))}
              {isArchived && <CheckLine tone="idle">{t("passport.checkArchived")}</CheckLine>}
            </ul>

            {/* The fix for every missing field lives on the product, so the list
                doubles as the jumping-off point to it. */}
            {missingLabels.length > 0 && (
              <div className="mt-4 rounded-lg border border-border bg-muted/40 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Text small secondary>
                    {t("passport.missingFields")}
                  </Text>
                  <Link
                    to={`/products/${passport.productId}`}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    {t("passport.openProduct")}
                    <Icon name="chevron-right" size={13} />
                  </Link>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {missingLabels.slice(0, MISSING_CHIP_LIMIT).map((label) => (
                    <span
                      key={label}
                      className="rounded-md border border-border bg-card px-2 py-0.5 text-xs text-foreground"
                    >
                      {label}
                    </span>
                  ))}
                  {missingLabels.length > MISSING_CHIP_LIMIT && (
                    <span className="px-1 py-0.5 text-xs text-muted-foreground">
                      {t("passport.moreFields", { count: missingLabels.length - MISSING_CHIP_LIMIT })}
                    </span>
                  )}
                </div>
              </div>
            )}
          </Card>

          <PassportVersionsCard orgId={org.id} passport={passport} onError={setError} />

          {canManageAccess && <AccessGrantsCard orgId={org.id} passportId={passport.id} />}

          <div className="grid gap-4 lg:grid-cols-2">
            <PassportLineageCard
              orgId={org.id}
              passport={passport}
              canWrite={canWrite}
              onSaved={reload}
              onError={setError}
            />
            <PassportRetentionCard
              orgId={org.id}
              passport={passport}
              canWrite={canWrite}
              onSaved={reload}
              onError={setError}
            />
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <PassportPublicAccessCard
            orgId={org.id}
            passport={passport}
            publicUrl={publicUrl}
            openLabel={openLabel}
            onError={setError}
          />

          {/* Hidden entirely when the API reports the registry feature unconfigured. */}
          {passport.registry.configured && (
            <PassportRegistryCard
              orgId={org.id}
              passport={passport}
              canPublish={canPublish}
              onChanged={reload}
              onError={setError}
            />
          )}

          {/* What the customs broker needs on the export declaration (draft TR
            ESPR Art. 17): the registry URI plus the commodity codes it is matched
            against. Shown whenever the passport is published — the codes come
            from the product even before registration. */}
          {isPublished && (
            <Card title={t("passport.customs")}>
              <Space direction="vertical" size="sm" style={{ width: "100%" }}>
                <Text small secondary>
                  {t("passport.customsHint")}
                </Text>
                <dl className="divide-y divide-border">
                  {registration ? (
                    <>
                      <MetaRow label={t("passport.customsUpi")}>
                        <Text code copyable={{ text: registration.uid }}>
                          {registration.uid}
                        </Text>
                      </MetaRow>
                      <MetaRow label={t("passport.customsUri")}>
                        {registration.registryUri ? (
                          <Text code copyable={{ text: registration.registryUri }}>
                            {registration.registryUri}
                          </Text>
                        ) : (
                          <Text small secondary>
                            {t("passport.customsUriPending")}
                          </Text>
                        )}
                      </MetaRow>
                    </>
                  ) : (
                    <MetaRow label={t("passport.customsUri")}>
                      <Text small secondary>
                        {t("passport.customsNone")}
                      </Text>
                    </MetaRow>
                  )}
                  <MetaRow label={t("passport.customsCn")}>
                    {registration?.commodityCode ?? product?.commodityCode ?? "—"}
                  </MetaRow>
                  <MetaRow label={t("passport.customsGtip")}>{product?.gtip ?? "—"}</MetaRow>
                </dl>
              </Space>
            </Card>
          )}

          <Card title={t("passport.identity")}>
            <dl className="divide-y divide-border">
              <MetaRow label={t("passports.product")}>
                {product ? (
                  <Link
                    to={`/products/${passport.productId}`}
                    className="text-primary hover:underline"
                    title={openLabel}
                  >
                    {product.name}
                  </Link>
                ) : (
                  "—"
                )}
              </MetaRow>
              <MetaRow label={t("products.schema")}>{def ? def.title[lang] : passport.schemaId}</MetaRow>
              {product && <MetaRow label={t("products.gtin")}>{product.gtin ?? "—"}</MetaRow>}
              {identifier && (
                <MetaRow label={t("passport.identifier")}>
                  <Text code copyable={{ text: identifier.value }}>
                    {identifier.value}
                  </Text>
                </MetaRow>
              )}
              <MetaRow label={t("passport.publicUrl")}>
                <Text code copyable={{ text: publicUrl }}>
                  {passport.publicSlug}
                </Text>
              </MetaRow>
            </dl>
          </Card>
        </div>
      </div>
    </div>
  );
}
